<!-- TEMPLATE -->
<template>
  <span 
    @click="itemClick"
    class="dean-section-title-item">
    <dean-tooltip 
      v-if="tooltip" 
      :class="deanSectionTitleItemClass">
      <span
        class="activator" slot="activator">
        <dean-icon
          class="item-icon"
          :glyph="icon"
          :viewBox="icon.viewBox"
          height="25"
          width="25"
        >
        </dean-icon>
        <span v-if="large" class="activator-text">
          <slot></slot>
        </span>
      </span>
      <slot></slot>
    </dean-tooltip>
    <div 
      v-else 
      :class="deanSectionTitleItemClass">
      <span
        class="activator" slot="activator">
        <dean-icon
          class="item-icon"
          :glyph="icon"
          :viewBox="icon.viewBox"
          height="25"
          width="25"
        >
        </dean-icon>
        <span v-if="large" class="activator-text">
          <slot></slot>
        </span>
      </span>
    </div>
  </span>
</template>

<!-- SCRIPT -->
<script lang="ts">
// VUE
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

// SVG
import empty from '../svg/icons/empty.svg';

// COMPONENTS
import deanIcon from './deanIcon.vue';
import deanTooltip from './deanTooltip.vue';

@Component({
  components: {
    deanIcon,
    deanTooltip
  }
})
export default class deanSectionTitleItem extends Vue {
  // PROPS
  @Prop() theme!: string;
  @Prop({ default: true }) tooltip!: boolean;
  @Prop({ default: false }) sidebar!: boolean;
  @Prop({ default: false }) content!: boolean;
  @Prop({ default: false }) result!: boolean;
  @Prop({ default: false }) active!: boolean;
  @Prop({ default: false }) highlight!: boolean;
  @Prop({ default: false }) disabled!: boolean;
  @Prop({ default: false }) large!: boolean;
  @Prop({
    default: function() {
      return empty;
    }
  })
  icon!: object;

  //COMPUTED
  get deanSectionTitleItemClass() {
    let value = 'container';
    if (this.sidebar) value += ` container-sidebar`;
    if (this.sidebar && this.highlight) value += ` container-sidebar-highlight`;
    if (this.sidebar && this.disabled) value += ` container-sidebar-disabled`;
    if (this.content) value += ` container-content`;
    if (this.content && this.highlight) value += ` container-content-highlight`;
    if (this.content && this.disabled) value += ` container-content-disabled`;
    if (this.active) value += ` active`;
    if (this.theme == 'themed') value += ` container-sidebar`;

    if (this.theme) {
      value += ` container-${this.theme}`;
    }
    return value;
  }

  //METHODS
  itemClick() {
    if (!this.disabled) {
      this.$emit('dean-section-title-item-click');
      this.$emit('deanSectionTitleItemClick');
    }
  }
}
</script>

<!-- STYLE -->
<style lang="scss" scoped>
//IMPORT
@import '../scss/vars';

// DEAN SECTION TITLE ITEM
.dean-section-title-item {
  height: 28px;
  display: inline-block;
  cursor: pointer;
  margin: 0px 5px 0px 5px;

  .container {
    @include display-flex;
    height: 28px;
    width: auto;
    justify-content: center;
    align-content: center;
    @include border-radius(5px);
    @include transition(background ease 0.8s);
    padding: 0px 2.5px 0px 2.5px;
    .activator {
      height: 28px;
      @include display-flex;
      .item-icon {
        display: inline-flex;
      }
      .activator-text {
        display: inline-flex;
        font-size: $font-large;
        word-break: keep-all;
        height: 28px;
        width: auto;
        padding: 5px 5px 5px 0px;
        font-weight: 700;
        white-space: nowrap;
        @media #{$small-only} {
          display: none;
        }
      }
    }
  }

  .container-sidebar {
    background: white;
    fill: var(--color-link, $color-link);
    color: var(--color-link, $color-link);
    @include box-shadow(0px 3px 5px rgba(0, 0, 0, 0.075));
    &:hover {
      background: var(--color-light, $color-light);
    }
    .item-icon {
      display: inline-flex;
      padding: 5px;
      width: 26px;
      height: 26px;
    }
  }

  .container-sidebar-highlight {
    background: var(--color-dark-shade-2, $color-dark-shade-2);
    fill: var(--color-light-shade-2, $color-light-shade-2);
    &:hover {
      background: var(--color-light-shade-2, $color-light-shade-2);
      color: var(--color-dark-shade-2, $color-dark-shade-2);
    }
    .item-icon {
      display: inline-flex;
      padding: 5px;
      width: 26px;
      height: 26px;
    }
  }

  .container-sidebar-disabled {
    border: 1px solid var(--color-light, $color-light);
    fill: var(--color-light, $color-light);
    background: var(--color-light-shade-3, $color-light-shade-3);
    &:hover {
      cursor: not-allowed;
    }
    .item-icon {
      display: inline-flex;
      padding: 5px;
      width: 26px;
      height: 26px;
    }
  }


  .container-content {
    background: white;
    fill: var(--color-link, $color-link);
    color: var(--color-link, $color-link);
    @include box-shadow(0px 3px 5px rgba(0, 0, 0, 0.075));
    border: 1px solid var(--color-light-shade-2, $color-light-shade-2);
    &:hover {
      background: scale-color(white, $lightness: -10%);
    }
    .item-icon {
      padding: 5px;
      width: 28px;
      height: 28px;
    }
  }

  .container-content-highlight {
    background: white;
    fill: var(--color-primary, $color-primary);
    color: var(--color-primary, $color-primary);
    &:hover {
      background: scale-color(white, $lightness: -10%);
    }
    .item-icon {
      padding: 5px;
      width: 28px;
      height: 28px;
    }
  }

  .container-content-disabled {
    background: rgba(255, 255, 255, 0.3);
    fill: rgba(255, 255, 255, 0.5);
    color: rgba(255, 255, 255, 0.5);
    &:hover {
      cursor: not-allowed;
    }
    .item-icon {
      padding: 5px;
      width: 28px;
      height: 28px;
    }
  }


  .active {
    background: white;
    fill: var(--color-primary, $color-primary);
    &:hover {
      background: rgba(255, 255, 255, 0.7);
    }
  }

  .container-secondary {
    border: 1px solid var(--color-light, $color-light);
    fill: var(--color-dark-shade-2, $color-dark-shade-2);
    background: white;

    &:hover {
      background: var(--color-light, $color-light);
    }
    .item-icon {
      padding: 5px;
      width: 26px;
      height: 26px;
    }
  }

  .item-icon {
    display: block;
    position: relative;
    @include transition(all ease 0.8s);
  }
}
</style>
